import { cloneElement, isValidElement, ReactElement, ReactNode } from 'react';
import trackEventManager from 'utils/event-manager';
import { DialogOpener } from 'ui-library/dialog';
import { AuthDialog, useAuthTrackingEvents } from 'scenes/auth';
import { SubscriptionCheckoutDialog } from 'scenes/subscription-checkout';
import { PageFeed } from 'model/indexTS';
import { PageState } from 'services/page';
import { CheckoutUnlock } from '../checkout-unlock';

export interface ActionWithLoginProps {
  isAuthenticaded: boolean;
  allowAnonymous?: boolean;
  feed: PageFeed;
  page: PageState;
  children: ReactNode;
  location: string;
}

const getChildren = ({ children, onClick }: { children: ReactNode; onClick: () => void }): ReactElement | null => {
  if (isValidElement(children) && children.props.renderOpener != null) {
    return (
      <>
        {children.props.renderOpener({
          onShow: onClick,
        })}
      </>
    );
  }

  if (isValidElement(children) && children.props.renderOpener == null) {
    return cloneElement(children, {
      onClick,
    });
  }

  return null;
};

export const ActionWithLogin = ({
  isAuthenticaded,
  children,
  location,
  feed,
  page,
  allowAnonymous = false,
}: ActionWithLoginProps): JSX.Element => {
  const { openModalTrackingEvent, closeModalTrackingEvent } = useAuthTrackingEvents();
  const componentKey = 'my_feed';

  if (!isAuthenticaded && !allowAnonymous) {
    return (
      <DialogOpener
        fullOnSmall
        renderOpener={({ onShow }) => {
          return getChildren({
            children,
            onClick: () => {
              onShow();
              openModalTrackingEvent(componentKey);
            },
          });
        }}
        renderDialogBody={({ onClose }) => (
          <AuthDialog onClose={onClose} componentKey={componentKey} location={location} />
        )}
        type="auth"
        onClose={() => closeModalTrackingEvent(componentKey)}
      />
    );
  }

  if (!feed.visible && feed.restrictedTo[0] === 'ONLY_SUBSCRIBERS' && !allowAnonymous) {
    return (
      <DialogOpener
        fullOnSmall
        renderOpener={({ onShow }) => {
          return getChildren({
            children,
            onClick: () => {
              onShow();
              trackEventManager.subscriptionPurchasedClick({
                location,
              });
            },
          });
        }}
        renderDialogBody={({ onClose }) => <SubscriptionCheckoutDialog onClose={onClose} />}
        onClose={() => closeModalTrackingEvent(componentKey)}
      />
    );
  }

  if (
    !feed.visible &&
    feed.restrictedTo[0] === 'PAID_CONTENT' &&
    feed.summary.paymentStatus !== 'InProgress' &&
    !allowAnonymous
  ) {
    return (
      <DialogOpener
        fullOnSmall
        renderOpener={({ onShow }) => {
          return getChildren({
            children,
            onClick: () => {
              onShow();
              trackEventManager.subscriptionPurchasedClick({
                location,
              });
            },
          });
        }}
        renderDialogBody={({ onClose }) => <CheckoutUnlock page={page} feed={feed} onClose={onClose} />}
        onClose={() => closeModalTrackingEvent(componentKey)}
      />
    );
  }

  return <>{children}</>;
};
